import { useState } from 'react';
import { Link } from 'react-router-dom';
import Breadcrumb from '../components/Breadcrumb';
import SectionEyebrow from '../components/SectionEyebrow';
import ImageSlot from '../components/ImageSlot';
import Reveal from '../components/Reveal';
import { lotTiers, type LotTier } from '../data/lots';

export default function Lots() {
  return (
    <div>
      {/* HERO: full-bleed photo */}
      <section className="relative min-h-[85vh] flex items-end overflow-hidden">
        <div className="absolute inset-0">
          <ImageSlot
            id="lots.hero"
            className="absolute inset-0 w-full h-full"
            aspect="auto"
            loading="eager"
          />
        </div>
        <div className="absolute inset-0 bg-gradient-to-b from-forest-950/40 via-forest-950/15 to-forest-950/80" />
        <div className="absolute inset-0 grain pointer-events-none" />

        <div className="relative z-10 w-full pb-20 lg:pb-24">
          <div className="max-w-7xl mx-auto px-6 lg:px-10 text-cream">
            <Breadcrumb current="Lots & Pricing" tone="dark" />
            <div className="mt-10 grid lg:grid-cols-12 gap-12 items-end">
              <div className="lg:col-span-8">
                <SectionEyebrow>
                  <span className="text-sand-300">Phase 1 . Open now</span>
                </SectionEyebrow>
                <h1 className="mt-6 font-display text-cream leading-[0.95] tracking-tight text-[clamp(2.75rem,7.5vw,7rem)]">
                  Find the lot that{' '}
                  <span className="italic text-sand-300">fits the life.</span>
                </h1>
                <p className="mt-8 max-w-2xl text-cream/85 text-lg leading-relaxed">
                  Four tiers, one community. From walkable village lots to
                  ridgeline trophy estates: each priced for who you are, not
                  what the market thinks you should pay.
                </p>
              </div>
              <div className="lg:col-span-4 space-y-5">
                <PhaseStat big="400 to 600" label="Total lots planned" />
                <PhaseStat big="40 to 60" label="Phase 1 release" />
                <PhaseStat big="7 to 10 yr" label="Build-out horizon" />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* PHASE 1 BAR */}
      <section className="bg-forest-700 text-cream py-6">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 flex flex-wrap items-center gap-6 justify-between">
          <div className="flex items-center gap-3">
            <span className="w-2 h-2 rounded-full bg-sand-300 animate-pulse" />
            <span className="font-display text-2xl">
              Phase 1 inventory{' '}
              <span className="text-cream/60">
                first 40 to 60 lots open
              </span>
            </span>
          </div>
          <div className="flex items-center gap-6 text-sm">
            <span className="text-cream/80">
              Refundable reservation deposit
            </span>
            <span className="w-px h-4 bg-cream/30" />
            <span className="text-sand-300">
              Founder pricing locked in
            </span>
          </div>
        </div>
      </section>

      {/* TIERS DETAIL: photo-led split */}
      <section className="py-24 lg:py-32 bg-cream">
        <div className="max-w-7xl mx-auto px-6 lg:px-10">
          <div className="max-w-3xl mb-20">
            <SectionEyebrow>A coherent village</SectionEyebrow>
            <h2 className="mt-6 font-display text-5xl lg:text-7xl leading-tight text-forest-900">
              One masterplan.{' '}
              <span className="italic text-clay-500">
                Four kinds of life.
              </span>
            </h2>
            <p className="mt-6 text-forest-900/80 text-lg leading-relaxed">
              Every tier sits in a distinct part of the property: different
              proximity to the river, different forest density, different
              price band.
            </p>
          </div>

          <div className="space-y-24 lg:space-y-32">
            {lotTiers.map((tier, idx) => (
              <Reveal key={tier.id}>
                <TierSection tier={tier} index={idx} />
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* COMPARISON TABLE */}
      <section className="py-24 lg:py-32 bg-forest-50">
        <div className="max-w-7xl mx-auto px-6 lg:px-10">
          <div className="max-w-3xl mb-12">
            <SectionEyebrow>Side by side</SectionEyebrow>
            <h2 className="mt-6 font-display text-5xl lg:text-7xl leading-tight text-forest-900">
              The four tiers,{' '}
              <span className="italic text-clay-500">compared.</span>
            </h2>
          </div>

          {/* Desktop table */}
          <div className="hidden lg:block overflow-x-auto">
            <table className="w-full text-left">
              <thead>
                <tr className="border-b-2 border-forest-900">
                  <th className="py-4 pr-4 eyebrow text-forest-900/60"></th>
                  {lotTiers.map((t) => (
                    <th key={t.id} className="py-4 px-4 align-bottom">
                      <div className="aspect-[4/3] mb-4 overflow-hidden">
                        <ImageSlot
                          id={`lots.${t.id}`}
                          className="w-full h-full"
                          aspect="auto"
                        />
                      </div>
                      <div className="font-display text-2xl text-forest-900">
                        {t.name.replace('The ', '')}
                      </div>
                      <div className="text-clay-600 text-sm">
                        {t.startingPrice}
                      </div>
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody className="divide-y divide-forest-900/10">
                <CompareRow label="Lot size" cells={lotTiers.map((t) => t.size)} />
                <CompareRow
                  label="Total lots"
                  cells={lotTiers.map((t) => t.totalLots)}
                />
                <CompareRow
                  label="Best for"
                  cells={lotTiers.map((t) => t.bestFor)}
                />
                <CompareRow
                  label="Private pool"
                  cells={lotTiers.map((t) => t.privatePool)}
                />
                <CompareRow
                  label="Build envelope"
                  cells={lotTiers.map((t) => t.buildEnvelope)}
                />
                <CompareRow
                  label="River access"
                  cells={lotTiers.map((t) => t.riverAccess)}
                />
                <CompareRow
                  label="Beach club"
                  cells={lotTiers.map((t) => t.beachClub)}
                />
                <CompareRow
                  label="Rental potential"
                  cells={lotTiers.map((t) => t.rentalPotential)}
                />
              </tbody>
            </table>
          </div>

          {/* Mobile cards */}
          <div className="lg:hidden space-y-6">
            {lotTiers.map((t) => (
              <div
                key={t.id}
                className="bg-cream border border-forest-900/10 overflow-hidden"
              >
                <div className="aspect-[16/9]">
                  <ImageSlot
                    id={`lots.${t.id}`}
                    className="w-full h-full"
                    aspect="auto"
                  />
                </div>
                <div className="p-6">
                  <div className="flex justify-between items-baseline">
                    <div className="font-display text-3xl text-forest-900">
                      {t.name.replace('The ', '')}
                    </div>
                    <div className="text-clay-600">{t.startingPrice}</div>
                  </div>
                  <dl className="mt-4 space-y-2 text-sm">
                    <CompareCell label="Lot size" value={t.size} />
                    <CompareCell label="Total lots" value={t.totalLots} />
                    <CompareCell label="Best for" value={t.bestFor} />
                    <CompareCell label="Private pool" value={t.privatePool} />
                    <CompareCell
                      label="Build envelope"
                      value={t.buildEnvelope}
                    />
                    <CompareCell label="River access" value={t.riverAccess} />
                    <CompareCell label="Beach club" value={t.beachClub} />
                    <CompareCell
                      label="Rental potential"
                      value={t.rentalPotential}
                    />
                  </dl>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* AMENITIES with photos */}
      <section className="py-24 lg:py-32 bg-cream">
        <div className="max-w-7xl mx-auto px-6 lg:px-10">
          <div className="max-w-3xl mb-16">
            <SectionEyebrow>What every owner gets</SectionEyebrow>
            <h2 className="mt-6 font-display text-5xl lg:text-7xl leading-tight text-forest-900">
              The lot is just{' '}
              <span className="italic text-clay-500">the beginning.</span>
            </h2>
            <p className="mt-6 text-forest-900/80 text-lg leading-relaxed">
              Every Nativa owner, Village or Signature, gets full access to
              the same community. The amenities do not tier with the price.
            </p>
          </div>

          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
            <Reveal>
              <Amenity
                imageId="lots.amenity.beach"
                title="Beach club access"
                body="Titled beach concession at the river mouth. Loungers, shaded palapas, kayaks and snorkel gear, simple food and bar. Open daily for owners."
              />
            </Reveal>
            <Reveal delay={100}>
              <Amenity
                imageId="lots.amenity.wellness"
                title="Wellness + fitness"
                body="Climate-adapted pickleball courts in shaded palapas. Open-air fitness pavilion. Yoga deck overlooking the river."
              />
            </Reveal>
            <Reveal delay={200}>
              <Amenity
                imageId="lots.amenity.trails"
                title="River-to-sea trails"
                body="Marked walking trails along the entire river spine. Quiet swim holes, observation platforms, horseback program."
              />
            </Reveal>
            <Reveal delay={100}>
              <Amenity
                imageId="lots.amenity.cacao"
                title="Cacao + farm-to-table"
                body="Working cacao orchard in partnership with Bribri growers. Farm-to-table restaurant with Caribbean-Costa Rican menu."
              />
            </Reveal>
            <Reveal delay={200}>
              <Amenity
                imageId="lots.amenity.security"
                title="24/7 security + gates"
                body="Main security gate with staffed welcome. Each of the eight neighborhoods has its own private gated entrance."
              />
            </Reveal>
            <Reveal delay={300}>
              <Amenity
                imageId="lots.amenity.concierge"
                title="Stewardship + concierge"
                body="Property management, rental coordination, maintenance, and a concierge for arrival and departure."
              />
            </Reveal>
          </div>
        </div>
      </section>

      {/* RESERVATION STEPS */}
      <section className="py-24 lg:py-32 bg-forest-900 text-cream relative overflow-hidden">
        <div className="absolute inset-0 opacity-15 grain" aria-hidden />
        <div className="max-w-7xl mx-auto px-6 lg:px-10 relative">
          <div className="max-w-3xl mb-16">
            <SectionEyebrow>
              <span className="text-sand-300">How reservation works</span>
            </SectionEyebrow>
            <h2 className="mt-6 font-display text-5xl lg:text-7xl leading-tight">
              From <span className="italic text-sand-300">interest</span> to
              keys, in clear steps.
            </h2>
            <p className="mt-6 text-cream/70 leading-relaxed">
              No high-pressure tactics. A refundable deposit gets you a
              reservation. You visit. You decide.
            </p>
          </div>

          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
            <Step
              n="1"
              title="Inquire"
              body="Fill out the founder list. Pick a tier and a price band. Briefly tell us how you would use it."
            />
            <Step
              n="2"
              title="Reserve"
              body="Refundable $5,000 deposit holds a tier slot in the queue. No specific lot committed yet."
            />
            <Step
              n="3"
              title="Visit"
              body="Walk the land with us. Walk the river. Choose your actual lot from the available inventory."
            />
            <Step
              n="4"
              title="Close"
              body="Costa Rica attorney handles title and SETENA filings. You get the deed. You are an owner."
            />
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="py-24 lg:py-32 bg-cream">
        <div className="max-w-4xl mx-auto px-6 lg:px-10">
          <SectionEyebrow>Common questions</SectionEyebrow>
          <h2 className="mt-6 font-display text-5xl lg:text-7xl leading-tight text-forest-900">
            The things most buyers{' '}
            <span className="italic text-clay-500">ask.</span>
          </h2>

          <div className="mt-12 space-y-2">
            {faqs.map((f, i) => (
              <FAQ key={i} q={f.q} a={f.a} />
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="relative py-32 overflow-hidden">
        <div className="absolute inset-0">
          <ImageSlot
            id="cta.closing"
            className="absolute inset-0 w-full h-full"
            aspect="auto"
          />
        </div>
        <div className="absolute inset-0 bg-cream/85" />
        <div className="relative max-w-5xl mx-auto px-6 lg:px-10 text-center">
          <div className="w-16 h-16 mx-auto mb-8 rounded-full bg-forest-700 flex items-center justify-center">
            <span className="font-display text-cream text-3xl leading-none">
              N
            </span>
          </div>
          <SectionEyebrow className="justify-center">
            Phase 1 . Open now
          </SectionEyebrow>
          <h2 className="mt-6 font-display text-4xl lg:text-7xl leading-tight text-forest-900">
            Choose your tier.{' '}
            <span className="italic text-clay-500">
              Choose your spot.
            </span>
          </h2>
          <p className="mt-6 max-w-2xl mx-auto text-forest-900/70 leading-relaxed">
            Refundable reservation deposit. Visit the land. Walk the river.
            Decide with your feet on the ground.
          </p>
          <div className="mt-10 flex flex-wrap gap-4 justify-center">
            <Link
              to="/reserve"
              className="px-7 py-4 bg-forest-700 text-cream font-medium tracking-wide hover:bg-forest-900 transition-colors"
            >
              Reserve a Lot
            </Link>
            <Link
              to="/calculator"
              className="px-7 py-4 border border-forest-900/30 text-forest-900 font-medium tracking-wide hover:bg-forest-900 hover:text-cream transition-colors"
            >
              Open the Calculator
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}

const faqs = [
  {
    q: 'Is the reservation deposit really refundable?',
    a: 'Yes, fully refundable until you sign a purchase agreement on a specific lot. The deposit holds your tier slot in the queue while you visit and decide. If you walk away, the money comes back. No questions, no penalties.',
  },
  {
    q: 'Can foreigners actually own land in Costa Rica?',
    a: 'Yes. Costa Rica grants foreign nationals the same property ownership rights as citizens. You can own land in your personal name or through a Costa Rican corporation (SRL or SA), which is the more common structure for international buyers and offers tax and liability advantages. Our recommended attorneys handle this routinely.',
  },
  {
    q: 'Do I have to build a home, or can I just hold the lot?',
    a: 'You can hold a lot without building, but design covenants set a build window, typically 5 to 7 years from purchase, to protect community coherence and prevent speculation. Lot value alone has historically appreciated meaningfully in well-planned CR communities, but buyers who build also unlock the rental income side of the equation.',
  },
  {
    q: 'What does it cost to actually build a home here?',
    a: "Construction costs in Costa Rica's Caribbean region currently run roughly $1,800 to $2,800 per square meter for quality construction, depending on materials, finish level, and design complexity. A 200 m2 Garden-tier home runs roughly $360K to $560K to build. We can connect you with vetted local architects and builders who understand the Nativa design covenants.",
  },
  {
    q: 'How does the Airbnb rental work?',
    a: 'Owners can rent independently or through the on-site stewardship program, which handles guest experience, cleaning, maintenance, and tax filings. The on-site eco-lodge drives year-round visitor traffic to the property. Net of management fees and operating costs, owners typically retain ~62% of gross rental.',
  },
  {
    q: 'What are the HOA fees?',
    a: 'Annual HOA fees are estimated at $4,800/year for Village and Garden tiers, scaling to ~$8,000/year for Estate and Signature. Fees cover security, common-area maintenance, beach club access, trail upkeep, and the stewardship fund that goes back into land restoration. Final fee schedule confirmed at closing of Phase 1.',
  },
  {
    q: 'What happens if Phase 1 sells out before I am ready?',
    a: 'Phase 2 will follow at higher pricing, typically 25 to 40% above Phase 1 entry. The single biggest financial advantage of being early is locking in the cost basis. If you are seriously considering Nativa but not ready to commit, the founder list keeps you in priority order for the next phase release.',
  },
  {
    q: 'How do I visit the property?',
    a: 'We host monthly visit weekends: three days, two nights, including transport from Limon airport, accommodation on-property in a partner lodge, and full guided exploration of the land, river, and beach concession. Schedule via the Reserve form below or email hello@costanativa.co directly.',
  },
];

/* SUBCOMPONENTS */

function PhaseStat({ big, label }: { big: string; label: string }) {
  return (
    <div className="flex items-baseline gap-4 border-b border-cream/20 pb-3">
      <div className="font-display text-3xl text-cream">{big}</div>
      <div className="text-xs uppercase tracking-widest-2 text-cream/70">
        {label}
      </div>
    </div>
  );
}

function TierSection({ tier, index }: { tier: LotTier; index: number }) {
  const reversed = index % 2 === 1;
  return (
    <div className="grid lg:grid-cols-12 gap-10 lg:gap-16 items-center">
      <div
        className={`lg:col-span-7 ${
          reversed ? 'lg:order-2' : ''
        } aspect-[4/3] overflow-hidden`}
      >
        <ImageSlot id={`lots.${tier.id}`} className="w-full h-full" aspect="auto" />
      </div>
      <div className={`lg:col-span-5 ${reversed ? 'lg:order-1' : ''}`}>
        <div className="eyebrow text-clay-600">
          {tier.number} . {tier.category}
        </div>
        <h3 className="mt-4 font-display text-4xl lg:text-6xl text-forest-900 leading-[1.05]">
          {tier.name}.
        </h3>
        <p className="mt-4 text-forest-900/70 italic text-lg">{tier.tagline}</p>

        <div className="mt-8 grid grid-cols-3 gap-6 border-t border-forest-900/10 pt-6">
          <Stat
            big={tier.size.split(' ')[0]}
            label={`Lot size m${'²'}`}
          />
          <Stat
            big={tier.startingPrice.replace('+', '')}
            label="Starting price"
            tone="clay"
          />
          <Stat big={tier.totalLots.split(' ')[0]} label="Total lots" />
        </div>

        <p className="mt-6 text-forest-900/80 leading-relaxed">
          {tier.description}
        </p>
        <p className="mt-4 text-sm">
          <strong className="text-forest-900">Best for:</strong>{' '}
          <span className="text-forest-900/70">{tier.bestFor}</span>
        </p>

        <div className="mt-8 flex flex-wrap gap-5">
          <Link
            to="/calculator"
            className="text-sm text-clay-600 underline underline-offset-4 hover:text-clay-700"
          >
            Run the numbers
          </Link>
          <Link
            to="/reserve"
            className="text-sm text-forest-900 font-medium hover:text-clay-600 inline-flex items-center gap-1"
          >
            Reserve this tier
          </Link>
        </div>
      </div>
    </div>
  );
}

function Stat({
  big,
  label,
  tone = 'forest',
}: {
  big: string;
  label: string;
  tone?: 'forest' | 'clay';
}) {
  return (
    <div>
      <div
        className={`font-display text-3xl ${
          tone === 'clay' ? 'text-clay-600' : 'text-forest-900'
        } leading-none`}
      >
        {big}
      </div>
      <div className="mt-2 text-xs uppercase tracking-widest-2 text-forest-700/70">
        {label}
      </div>
    </div>
  );
}

function CompareRow({ label, cells }: { label: string; cells: string[] }) {
  return (
    <tr className="hover:bg-cream/50">
      <td className="py-4 pr-4 eyebrow text-forest-900/60">{label}</td>
      {cells.map((c, i) => (
        <td key={i} className="py-4 px-4 text-forest-900/80">
          {c}
        </td>
      ))}
    </tr>
  );
}

function CompareCell({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex items-baseline justify-between gap-4 border-b border-forest-900/10 pb-2">
      <dt className="text-xs uppercase tracking-widest-2 text-forest-700/70">
        {label}
      </dt>
      <dd className="text-forest-900 text-right">{value}</dd>
    </div>
  );
}

function Amenity({
  imageId,
  title,
  body,
}: {
  imageId: string;
  title: string;
  body: string;
}) {
  return (
    <div className="group bg-cream border border-forest-900/10 overflow-hidden card-lift h-full flex flex-col">
      <div className="aspect-[4/3] overflow-hidden">
        <ImageSlot id={imageId} className="w-full h-full" aspect="auto" />
      </div>
      <div className="p-7 flex-1 flex flex-col">
        <h3 className="font-display text-2xl text-forest-900">{title}</h3>
        <p className="mt-3 text-forest-900/70 text-sm leading-relaxed flex-1">
          {body}
        </p>
      </div>
    </div>
  );
}

function Step({ n, title, body }: { n: string; title: string; body: string }) {
  return (
    <div className="border border-cream/15 bg-forest-950/40 p-6 lg:p-8 backdrop-blur-sm">
      <div className="font-display text-5xl text-sand-300 leading-none">
        {n}
      </div>
      <h3 className="mt-6 font-display text-2xl text-cream">{title}</h3>
      <p className="mt-3 text-cream/70 text-sm leading-relaxed">{body}</p>
    </div>
  );
}

function FAQ({ q, a }: { q: string; a: string }) {
  const [open, setOpen] = useState(false);
  return (
    <div className="border-b border-forest-900/10">
      <button
        onClick={() => setOpen((v) => !v)}
        className="w-full flex items-center justify-between py-6 text-left group"
        aria-expanded={open}
      >
        <span className="font-display text-xl lg:text-2xl text-forest-900 pr-6 group-hover:text-clay-600 transition-colors">
          {q}
        </span>
        <span
          className={`text-clay-500 text-2xl flex-shrink-0 transition-transform ${
            open ? 'rotate-45' : ''
          }`}
          aria-hidden
        >
          +
        </span>
      </button>
      {open && (
        <div className="pb-6 pr-12 text-forest-900/80 leading-relaxed">{a}</div>
      )}
    </div>
  );
}
